import React from 'react';
import recordingStartChangelog from './recording-start.changes.md';

import { HandleType, NodeToolbar, Position } from 'reactflow';
import { useSelector } from 'react-redux';
import { Command, selectCommandByNodeId, State, tabActions } from '@store/index';
import { Stack } from '@mui/material';
import { NodeWebhookAnchors } from '@common/Node/Defaults/NodeWebhookAnchors';
import { useNodeError } from '@common/hooks/useNodeError';
import { NodeContainer, NodeErrorToolbar, NodeHeader } from '@common/Node';
import { useNodeOutline } from '@common/hooks/useNodeOutline';
import { AnchorType } from '@components/Flow';
import { AnyAction, Dispatch } from '@reduxjs/toolkit';

import {
  NodeConfig,
  NodeField,
  NodeFieldType,
  NodeInputType,
  NodeOutput,
  NodeType,
  RecordingStartNodeState,
} from '../..';

export const config: NodeConfig = {
  version: '1.0',
  title: 'Recording Start',
  identifier: 'RecordingStart',
  command_id: 'call.recording.start',
  icon: 'telnyx',
  background: '#000000',
  changelog: recordingStartChangelog,
  anchors: [
    {
      label: 'Execute',
      id: 'call.recording.start.execute',
      type: 'target' as HandleType,
      variant: 'execution',
      isHandle: true,
    },
    {
      label: 'Data',
      id: 'call.recording.start.input_data',
      type: 'target' as HandleType,
      variant: 'data',
      isHandle: true,
    },
    {
      id: 'call.recording.started',
      label: 'call.recording.started',
      type: 'source' as HandleType,
      variant: 'webhook_execution',
    },
    {
      label: 'Data',
      id: 'call.recording.started.output_data',
      type: 'source' as HandleType,
      variant: 'data',
    },
    {
      label: 'call.recording.saved',
      id: 'call.recording.saved',
      type: 'source' as HandleType,
      variant: 'webhook_execution',
    },
    {
      label: 'Data',
      id: 'call.recording.saved.output_data',
      type: 'source' as HandleType,
      variant: 'data',
    },
    {
      label: 'call.recording.error',
      id: 'call.recording.error',
      type: 'source' as HandleType,
      variant: 'webhook_execution',
    },
    {
      label: 'Data',
      id: 'call.recording.error.output_data',
      type: 'source' as HandleType,
      variant: 'data',
    },
    {
      label: 'call.recording.transcription.saved',
      id: 'call.recording.transcription.saved',
      type: 'source' as HandleType,
      variant: 'webhook_execution',
    },
    {
      label: 'Data',
      id: 'call.recording.transcription.saved.output_data',
      type: 'source' as HandleType,
      variant: 'data',
    },
  ],
  webhooks: [
    {
      webhook: 'call.recording.saved',
      expected: true,
      multi: false,
    },
    {
      webhook: 'call.recording.transcription.saved',
      expected: false,
      multi: false,
    },
    {
      webhook: 'call.recording.error',
      expected: false,
      multi: false,
    },
  ],
};

export const state: RecordingStartNodeState = {
  call_control_id: { type: NodeInputType.Expression, value: '' },
  client_state: { type: NodeInputType.Expression, value: '' },
  format: 'mp3',
  channels: 'dual',
  play_beep: 'true',
  custom_file_name: { type: NodeInputType.Expression, value: '' },
  max_length: { type: NodeInputType.Number, value: '0' },
  timeout_secs: { type: NodeInputType.Number, value: '0' },
  recording_track: 'both',
  trim: 'disabled',
  transcription: 'false',
  transcription_engine: 'A',
  transcription_language_google: 'en',
  transcription_language_telnyx: 'en',
  transcription_profanity_filter: 'false',
  transcription_speaker_diarization: 'false',
  transcription_min_speaker_count: { type: NodeInputType.Number, value: '2' },
  transcription_max_speaker_count: { type: NodeInputType.Number, value: '6' },
};

export const fields: NodeField[] = [
  {
    id: 'call_control_id',
    tab: '1',
    type: NodeFieldType.InputExpression,
    label: 'Call Control ID',
    description: 'Unique identifier and token for controlling the call',
    placeholder: 'A unique identifier for controlling the call',
    required: false,
  },
  {
    id: 'format',
    tab: '1',
    type: NodeFieldType.Toggle,
    label: 'Recording Format',
    description: 'The audio file format used when storing the call recording.',
    options: [
      { label: 'MP3', value: 'mp3' },
      { label: 'WAV', value: 'wav' },
    ],
    required: true,
  },
  {
    id: 'channels',
    tab: '1',
    type: NodeFieldType.Toggle,
    label: 'Recording Channels',
    description:
      'When dual, final audio file will be stereo recorded with the first leg on channel A, and the rest on channel B.',
    options: [
      { label: 'Single', value: 'single' },
      { label: 'Dual', value: 'dual' },
    ],
    required: true,
  },
  {
    id: 'play_beep',
    tab: '1',
    type: NodeFieldType.Toggle,
    label: 'Play Beep',
    description: 'If enabled, a beep sound will be played at the start of a recording.',
    options: [
      { label: 'Enabled', value: 'true' },
      { label: 'Disabled', value: 'false' },
    ],
    required: false,
  },
  {
    id: 'custom_file_name',
    tab: '1',
    type: NodeFieldType.InputExpression,
    label: 'Recording File Name',
    description:
      'The custom recording file name to be used instead of the default call_leg_id. Telnyx will still add a Unix timestamp suffix. Must be less than 40 characters.',
    placeholder: 'Give your recording file a name (max 40 characters).',
    required: false,
  },
  {
    id: 'recording_track',
    tab: '1',
    type: NodeFieldType.Option,
    label: 'Recording Track',
    description:
      'The audio track to be recorded. Can be either both, inbound or outbound. If only single track is specified (inbound, outbound), channels configuration is ignored and it will be recorded as mono (single channel).',
    options: [
      { label: 'Both', value: 'both' },
      { label: 'Inbound', value: 'inbound' },
      { label: 'Outbound', value: 'outbound' },
    ],
    required: false,
  },
  {
    id: 'trim',
    tab: '1',
    type: NodeFieldType.Option,
    label: 'Trim Recording',
    description: 'When set to trim-silence, silence will be removed from the beginning and end of the recording.',
    options: [
      { label: 'Disabled', value: 'disabled' },
      { label: 'Trim Silence', value: 'trim-silence' },
    ],
    required: false,
  },
  {
    id: 'max_length',
    tab: '1',
    type: NodeFieldType.NumberExpression,
    label: 'Max Recording Length',
    description:
      'Defines the maximum length for the recording in seconds. The minimum value is 0. The maximum value is 14400. The default value is 0 (infinite)',
    placeholder: 'Define the max recording length. 0 is infinite.',
    required: false,
    minNumber: 0,
    maxNumber: 14400,
  },
  {
    id: 'timeout_secs',
    tab: '1',
    type: NodeFieldType.NumberExpression,
    label: 'Recording Timeout (secs)',
    description:
      'The number of seconds that Telnyx will wait for the recording to be stopped if silence is detected. The timer only starts when the speech is detected. Please note that call transcription is used to detect silence and the related charge will be applied. The minimum value is 0. The default value is 0 (infinite)',
    placeholder: 'Define silence duration before terminating recording. 0 is infinite.',
    required: false,
    minNumber: 0,
  },
  {
    id: 'transcription',
    type: NodeFieldType.Toggle,
    label: 'Transcription',
    description: 'Enable post recording transcription. The default value is false.',
    options: [
      { label: 'Enabled', value: 'true' },
      { label: 'Disabled', value: 'false' },
    ],
    required: false,
  },
  {
    id: 'transcription_engine',
    tab: 'transcription',
    type: NodeFieldType.Option,
    label: 'Transcription Engine',
    description: 'Engine to use for speech recognition.',
    options: [
      { label: 'Google', value: 'A' },
      { label: 'Telnyx', value: 'B' },
    ],
    required: false,
  },
  // {
  //   id: 'transcription_language_telnyx',
  //   tab: 'transcription',
  //   type: NodeFieldType.Option,
  //   options: telnyxLanguageOptions,
  //   label: 'Telnyx Transcription Language',
  //   description: 'Language to use for speech recognition.',
  //   required: false,
  //   placeholder: 'Language to use for speech recognition, default English.',
  // },
  // {
  //   id: 'transcription_language_google',
  //   tab: 'transcription',
  //   type: NodeFieldType.Option,
  //   options: googleLanguageOptions,
  //   label: 'Google Transcription Language',
  //   description: 'Language to use for speech recognition.',
  //   required: false,
  //   placeholder: 'Language to use for speech recognition, default English.',
  // },
  {
    id: 'transcription_min_speaker_count',
    tab: 'transcription',
    type: NodeFieldType.NumberExpression,
    label: 'Transcription Min Speaker Count',
    description: 'Defines minimum number of speakers in the conversation. Applies to google engine only.',
    placeholder: 'Minimum Transcription Speakers.',
    required: false,
  },
  {
    id: 'transcription_max_speaker_count',
    tab: 'transcription',
    type: NodeFieldType.NumberExpression,
    label: 'Transcription Max Speaker Count',
    description: 'Defines maximum number of speakers in the conversation. Applies to google engine only.',
    placeholder: 'Maximum Transcription Speakers.',
    required: false,
  },
  {
    id: 'transcription_profanity_filter',
    tab: 'transcription',
    type: NodeFieldType.Toggle,
    label: 'Transcription Profanity Filter',
    description: 'Enables profanity_filter. Applies to google engine only.',
    options: [
      { label: 'Enabled', value: 'true' },
      { label: 'Disabled', value: 'false' },
    ],
    required: false,
  },
  {
    id: 'transcription_speaker_diarization',
    tab: 'transcription',
    type: NodeFieldType.Toggle,
    label: 'Transcription Speaker Diarization',
    description: 'Enables speaker diarization. Applies to google engine only.',
    options: [
      { label: 'Enabled', value: 'true' },
      { label: 'Disabled', value: 'false' },
    ],
    required: false,
  },
  {
    id: 'client_state',
    tab: '1',
    type: NodeFieldType.InputExpression,
    label: 'Client State',
    description: 'Use this field to add state to every subsequent webhook. It must be a valid Base-64 encoded string.',
    placeholder: 'Add state to each subsequent webhook.',
    required: false,
  },
];

export const output: NodeOutput = {
  default: {
    'call.recording.started': {
      call: {
        recording: {
          started: {
            result: 'string',
            record_type: 'string',
            recording_id: 'string',
            call_control_id: 'string',
          },
        },
      },
    },
    'call.recording.saved': {
      call: {
        recording: {
          saved: {
            call_control_id: 'string',
            call_leg_id: 'string',
            call_session_id: 'string',
            client_state: 'string | null',
            connection_id: 'number',
            recording_started_at: 'date-time',
            recording_ended_at: 'date-time',
            channels: "'single' | 'dual'",
            recording_urls: {
              mp3: 'string | null',
              wav: 'string | null',
            },
            public_recording_urls: {
              mp3: 'string | null',
              wav: 'string | null',
            },
          },
        },
      },
    },
    'call.recording.transcription.saved': {
      call: {
        recording: {
          transcription: {
            saved: {
              call_control_id: 'string',
              call_leg_id: 'string',
              call_session_id: 'string',
              client_state: 'string | null',
              connection_id: 'number',
              recording_id: 'string',
              recording_transcription_id: 'string',
              status: 'string',
              transcription_text: 'string',
            },
          },
        },
      },
    },
    'call.recording.error': {
      call: {
        recording: {
          error: {
            call_control_id: 'string',
            call_leg_id: 'string',
            call_session_id: 'string',
            client_state: 'string | null',
            connection_id: 'number',
            reason: 'string',
          },
        },
      },
    },
  },
  dynamic: null,
};

export const RecordingStart: React.FC<NodeType> = ({ id, selected, data }) => {
  const { title, background, anchors, icon } = config;

  const command = useSelector((state: State) => selectCommandByNodeId(state, id));

  const { isNodeError, nodeError, nodeStatus } = useNodeError({ command });
  const { nodeOutline } = useNodeOutline({ command, selected });

  if (!command) return null;
  if (command.config.command_id !== 'call.recording.start') return null;

  const state = command?.state as RecordingStartNodeState;
  const isToolbarVisible = Boolean(isNodeError);

  return (
    <NodeContainer selected={selected} nodeOutline={nodeOutline} error={false}>
      <NodeToolbar offset={18} isVisible={isToolbarVisible} position={Position.Top}>
        <Stack direction='row' spacing={1}>
          {isNodeError && <NodeErrorToolbar error={nodeError} />}
        </Stack>
      </NodeToolbar>
      <NodeHeader id={id} title={title} icon={icon} background={background} status={nodeStatus} />
      <NodeWebhookAnchors id={id} command={command} data={data} anchors={getRecordingStartAnchors(anchors, state)} />
    </NodeContainer>
  );
};

const getRecordingStartAnchors = (anchors: AnchorType[], state: RecordingStartNodeState) => {
  const isTranscriptionEnabled = state?.transcription === 'true';

  return anchors.filter((anchor) => {
    if (!isTranscriptionEnabled && anchor.id.includes('transcription')) return false;
    return true;
  });
};

export const customRecordingBehaviour = (
  value: string | string[],
  field: NodeField,
  command: Command,
  dispatch: Dispatch<AnyAction>
) => {
  const nodeId = command.node.id;

  if (field.id === 'transcription' && command.config.command_id === 'call.recording.start') {
    if (value === 'true') {
      dispatch(tabActions.nodeWidthUpdate({ nodeId, width: 400 }));
    } else {
      const edgesToRemove = ['call.recording.transcription.saved', 'call.recording.transcription.saved.output_data'];

      // delete any connected edges if disabling
      for (const edge of edgesToRemove) dispatch(tabActions.removeEdgeByHandle({ node_id: nodeId, handle: edge }));
      dispatch(tabActions.nodeWidthUpdate({ nodeId, width: 320 }));
    }
  }
};

export default { config, state, fields, output };
